import { Suspense, useEffect, useRef } from "react";
import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { config } from "@/lib/config";
import { useAuth } from "@/features/auth/AuthContext";
import { AuthMenu } from "@/components/auth/AuthMenu";
import { MessageCenter } from "@/components/notifications/MessageCenter";
import styles from "./Layout.module.css";

// Top-level (non-space) paths — anything else that's a single segment is a space slug.
const RESERVED = new Set([
  "", "login", "register", "verify-email", "forgot-password",
  "reset-password", "profile", "health", "tools",
]);

const sections = [
  { key: "pets", label: "Pet Care" },
  { key: "reading", label: "Reading" },
  { key: "travel", label: "Travel" },
  { key: "cooking", label: "Cooking" },
  { key: "diary", label: "Diary" },
  { key: "guides", label: "Guides" },
];

export function Layout() {
  const { isAdmin } = useAuth();
  const { pathname } = useLocation();
  const navRef = useRef<HTMLElement>(null);

  // The current space slug, if we're on a `/:slug/...` route (not a reserved path).
  const firstSeg = pathname.split("/")[1] ?? "";
  const spaceSlug = RESERVED.has(firstSeg) ? null : firstSeg;

  // On phones the nav scrolls sideways — keep the active link in view, or the
  // current section (e.g. Diary, last in the row) sits half cut off screen.
  useEffect(() => {
    const nav = navRef.current;
    if (!nav || nav.scrollWidth <= nav.clientWidth) return;
    const active = nav.querySelector<HTMLElement>(`.${styles.navLinkActive}`);
    if (!active) return;
    const target = active.offsetLeft - (nav.clientWidth - active.offsetWidth) / 2;
    nav.scrollLeft = Math.max(0, Math.min(target, nav.scrollWidth - nav.clientWidth));
  }, [pathname]);

  return (
    <div className={styles.root}>
      <header className={styles.header}>
        <div className={`container ${styles.headerInner}`}>
          <Link to={spaceSlug ? `/${spaceSlug}` : "/"} className={styles.brand}>
            {config.appName}
          </Link>
          <nav ref={navRef} className={styles.nav} aria-label="Main navigation">
            {spaceSlug && (
              <>
                <NavLink
                  to={`/${spaceSlug}`}
                  end
                  className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ""}`}
                >
                  Home
                </NavLink>
                {sections.map((item) => (
                  <NavLink
                    key={item.key}
                    to={`/${spaceSlug}/${item.key}`}
                    className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ""}`}
                  >
                    {item.label}
                  </NavLink>
                ))}
              </>
            )}
            {/* Public + space-independent, so it shows on every page — including
                the landing page, where there is no slug and no section links. */}
            <NavLink
              to="/tools"
              className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ""}`}
            >
              Tools
            </NavLink>
            {isAdmin && (
              <NavLink
                to="/health"
                className={({ isActive }) => `${styles.navLink} ${isActive ? styles.navLinkActive : ""}`}
              >
                System Health
              </NavLink>
            )}
          </nav>
          {config.stage !== "prod" && (
            <span className={styles.stageBadge}>{config.stage}</span>
          )}
          {isAdmin && <MessageCenter />}
          <AuthMenu />
        </div>
      </header>

      <main className={styles.main}>
        <Suspense fallback={<div style={{ padding: "4rem", textAlign: "center" }} aria-busy="true">Loading…</div>}>
          <Outlet />
        </Suspense>
      </main>

      <footer className={styles.footer}>
        <div className="container">
          <p>Made with love by Allen &amp; family · {new Date().getFullYear()}</p>
        </div>
      </footer>
    </div>
  );
}
